// Partner dashboard — secured by magic-link token (?t=...).
// Shows: discount code, status banner, totals, Meta-generated orders
// (privacy-minimised), per-order list with commission, monthly-fee
// deferred card (post-trial review), contract download + tick-to-accept.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Wallet, ShieldCheck, FileText, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};

export default function PartnerDashboard() {
  const loc = useLocation();
  const token = useMemo(() => new URLSearchParams(loc.search).get("t"), [loc.search]);

  const [data, setData] = useState({ me: null, stats: null, orders: [], metaOrders: null });
  const { me, stats, orders, metaOrders } = data;
  const [err, setErr] = useState(null);
  // Tick-to-accept UI state
  const [acceptTicked, setAcceptTicked] = useState(false);
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);
  const [acceptErr, setAcceptErr] = useState(null);

  useEffect(() => {
    if (!token) { setErr("Missing sign-in token. Please use the link from your email."); return; }
    let cancelled = false;
    (async () => {
      try {
        const [m, s, o, mo] = await Promise.all([
          axios.get(`${API}/partner/me`, { params: { t: token } }),
          axios.get(`${API}/partner/stats`, { params: { t: token } }),
          axios.get(`${API}/partner/orders`, { params: { t: token } }),
          axios.get(`${API}/partner/meta-orders`, { params: { t: token } }),
        ]);
        if (cancelled) return;
        setData({ me: m.data, stats: s.data, orders: o.data, metaOrders: mo.data });
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Failed to load dashboard.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const acceptContract = async () => {
    if (!acceptTicked) {
      setAcceptErr("Please tick the box to confirm you accept the Partner Agreement.");
      return;
    }
    setAcceptSubmitting(true);
    setAcceptErr(null);
    try {
      const res = await axios.post(
        `${API}/partner/accept-contract`,
        { accepted: true },
        { params: { t: token } },
      );
      setData((cur) => ({
        ...cur,
        me: { ...cur.me, contract_signed_at: res.data?.contract_signed_at || new Date().toISOString() },
      }));
    } catch (e) {
      setAcceptErr(e?.response?.data?.detail || "Failed to record acceptance. Please try again.");
    } finally {
      setAcceptSubmitting(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16">
        <div className="pg-card p-7 border-[#FF4E5A]/40">
          <AlertCircle size={24} className="text-[#FF4E5A] mb-2" />
          <div className="text-[#F7FAFC] text-lg font-semibold mb-1">Unable to sign you in.</div>
          <div className="text-[#9CA8B8] text-sm">{err}</div>
          <Link to="/partner/login" className="pg-btn-ghost mt-5 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }
  if (!me || !stats) return <div className="px-5 py-16 text-[#9CA8B8]">Loading…</div>;

  const statusChip = me.status === "active"
    ? { label: "ACTIVE", color: "#A6FF00" }
    : me.status === "trial"
    ? { label: "TRIAL", color: "#5EEAD4" }
    : me.status === "paused"
    ? { label: "PAUSED", color: "#F5A524" }
    : { label: "TERMINATED", color: "#FF4E5A" };

  const contractAccepted = !!me.contract_signed_at;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="partner-dashboard">
      {/* Header */}
      <header className="mb-10">
        <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4] mb-1">// Partner Dashboard</div>
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-[#F7FAFC] text-3xl sm:text-4xl pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
            {me.company_name}
          </h1>
          <span
            className="pg-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded-sm border"
            style={{ color: statusChip.color, borderColor: statusChip.color + "55", background: statusChip.color + "11" }}
            data-testid="partner-status-chip"
          >
            {statusChip.label}
          </span>
        </div>
        {me.status === "trial" && (me.trial_starts_at || me.trial_ends_at) && (
          <p className="text-[#9CA8B8] text-sm mt-2" data-testid="partner-trial-window">
            30-day trial:{" "}
            <strong className="text-[#F7FAFC]">{fmtDate(me.trial_starts_at)}</strong>
            {" → "}
            <strong className="text-[#F7FAFC]">{fmtDate(me.trial_ends_at)}</strong>
            . Post-trial commercial terms reviewed in writing under clause 5.
          </p>
        )}
      </header>

      {/* Code + Commission % strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <div className="pg-card p-5">
          <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4] mb-1">Your Discount Code</div>
          <div className="text-[#A6FF00] text-3xl pg-mono font-bold tracking-wider" data-testid="partner-code">{me.discount_code}</div>
          <div className="text-[#9CA8B8] text-xs mt-1">Customers type this at checkout for 10% off.</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4] mb-1">Commission Rate</div>
          <div className="text-[#F7FAFC] text-3xl pg-heading" style={{ fontFamily: "'Rajdhani'" }}>{me.commission_pct}%</div>
          <div className="text-[#9CA8B8] text-xs mt-1">Of subtotal after customer discount.</div>
        </div>
        <div className="pg-card p-5" data-testid="partner-monthly-fee-card">
          <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4] mb-1">Monthly Fee</div>
          <div className="text-[#F7FAFC] text-3xl pg-heading" style={{ fontFamily: "'Rajdhani'" }}>Deferred</div>
          <div className="text-[#9CA8B8] text-xs mt-1">No charges during the 30-day trial. To be reviewed in writing post-trial (clause 5).</div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={TrendingUp} label="Orders (all time)" value={stats.orders_count} data-testid="partner-stat-orders" />
        <Stat icon={TrendingUp} label="Orders (last 30 days)" value={stats.orders_count_30d} />
        <Stat icon={Wallet} label="Commission earned" value={fmt(stats.commission_earned_gbp)} accent data-testid="partner-stat-commission-earned" />
        <Stat icon={Wallet} label="Currently due" value={fmt(stats.commission_due_gbp)} accent data-testid="partner-stat-commission-due" />
      </div>

      {/* Meta-generated orders — tracked Meta-ads attribution, new customers only */}
      <section className="mb-12" data-testid="partner-meta-orders-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[#F7FAFC] text-2xl pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
            Meta-Generated Orders {metaOrders ? `(${metaOrders.orders_count})` : ""}
          </h2>
          <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">new customers · paid only</span>
        </div>
        <p className="text-[#9CA8B8] text-[13px] leading-[1.7] mb-4">
          Orders tracked to Meta advertising (Facebook / Instagram), whether or not the customer used your
          discount code. Only first-time customers count — returning customers are excluded. Customer details
          are withheld under UK GDPR.
        </p>
        {metaOrders && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <Stat icon={TrendingUp} label="Meta orders" value={metaOrders.orders_count} data-testid="partner-meta-stat-orders" />
            <Stat icon={TrendingUp} label="Total order value" value={fmt(metaOrders.total_value_gbp)} data-testid="partner-meta-stat-value" />
            <Stat icon={Wallet} label={`Total commission (${metaOrders.commission_pct}%)`} value={fmt(metaOrders.total_commission_gbp)} accent data-testid="partner-meta-stat-commission" />
          </div>
        )}
        {!metaOrders || metaOrders.orders.length === 0 ? (
          <div className="pg-card p-6 text-center text-[#9CA8B8]" data-testid="partner-meta-orders-empty">
            No Meta-generated orders yet. Paid orders from new customers arriving via your Meta ads will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-meta-orders-table">
              <thead>
                <tr className="text-[#5EEAD4] pg-mono text-[10px] uppercase tracking-[0.18em] border-b border-[#1E2A44]">
                  <th className="text-left py-3 pr-4">Order</th>
                  <th className="text-left py-3 pr-4">Date &amp; Time</th>
                  <th className="text-right py-3">Order Value</th>
                </tr>
              </thead>
              <tbody>
                {metaOrders.orders.map((o, i) => (
                  <tr key={o.order_number || i} className="border-b border-[#1E2A44]/50 hover:bg-[#0B1322]/40 transition-colors">
                    <td className="py-3 pr-4 font-mono text-[#F7FAFC] text-[12px]">{o.order_number}</td>
                    <td className="py-3 pr-4 text-[#9CA8B8] text-[12px]">{fmtDateTime(o.placed_at)}</td>
                    <td className="py-3 text-right text-[#F7FAFC] font-semibold text-[13px]">{fmt(o.value_gbp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Orders table */}
      <section data-testid="partner-orders-table">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[#F7FAFC] text-2xl pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
            Attributed Orders ({orders.length})
          </h2>
          <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">paid only</span>
        </div>
        {orders.length === 0 ? (
          <div className="pg-card p-6 text-center text-[#9CA8B8]">
            No orders yet attributed to <span className="pg-mono text-[#A6FF00]">{me.discount_code}</span>.
            <br />Once a customer pays an order with your code, it will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#5EEAD4] pg-mono text-[10px] uppercase tracking-[0.18em] border-b border-[#1E2A44]">
                  <th className="text-left py-3 pr-4">Order</th>
                  <th className="text-left py-3 pr-4">Paid</th>
                  <th className="text-left py-3 pr-4">Items</th>
                  <th className="text-right py-3 pr-4">Subtotal</th>
                  <th className="text-right py-3 pr-4">After Discount</th>
                  <th className="text-right py-3">Commission</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr key={o.order_number || i} className="border-b border-[#1E2A44]/50 hover:bg-[#0B1322]/40 transition-colors">
                    <td className="py-3 pr-4 font-mono text-[#F7FAFC] text-[12px]">{o.order_number}</td>
                    <td className="py-3 pr-4 text-[#9CA8B8] text-[12px]">{fmtDate(o.paid_at || o.created_at)}</td>
                    <td className="py-3 pr-4 text-[#D5DCE8] text-[12px]">
                      {(o.items || []).map((it, j) => (
                        <div key={j}>{it.quantity}× {it.name} {it.strength}</div>
                      ))}
                    </td>
                    <td className="py-3 pr-4 text-right text-[#9CA8B8] text-[12px]">{fmt(o.subtotal_gbp)}</td>
                    <td className="py-3 pr-4 text-right text-[#F7FAFC] text-[12px]">{fmt(o.commission_base_gbp)}</td>
                    <td className="py-3 text-right text-[#A6FF00] font-semibold text-[13px]">{fmt(o.commission_gbp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Contract download + tick-to-accept */}
      <section className="mt-12 pt-8 border-t border-[#1E2A44]" data-testid="partner-contract-section">
        <h2 className="text-[#F7FAFC] text-xl pg-heading mb-2" style={{ fontFamily: "'Rajdhani'" }}>Partner Agreement</h2>
        <p className="text-[#9CA8B8] text-sm mb-4 leading-relaxed">
          Current contract: <strong className="text-[#F7FAFC]">{me.contract_version}</strong>
          {contractAccepted
            ? ` · Accepted electronically on ${fmtDate(me.contract_signed_at)}.`
            : " · Awaiting your acceptance below."}
        </p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/partner/contract?t=${token}`}
          className="pg-btn-ghost inline-flex items-center gap-2"
          data-testid="partner-contract-download"
          target="_blank"
          rel="noreferrer"
        >
          <FileText size={16} /> Download Contract PDF <ExternalLink size={12} />
        </a>

        {contractAccepted ? (
          <div className="mt-6 p-5 rounded-sm border border-[#A6FF00]/30 bg-[#A6FF00]/[0.06] flex items-start gap-3"
               data-testid="partner-contract-accepted-banner">
            <CheckCircle2 size={20} className="text-[#A6FF00] shrink-0 mt-0.5" />
            <div>
              <div className="text-[#F7FAFC] font-semibold mb-1">Agreement accepted</div>
              <p className="text-[#9CA8B8] text-[13px] leading-[1.7]">
                You accepted contract <strong className="text-[#F7FAFC]">{me.contract_version}</strong> on{" "}
                <strong className="text-[#F7FAFC]">{fmtDate(me.contract_signed_at)}</strong>. A copy is available
                via the download link above. Your acceptance has been recorded with timestamp and IP for audit purposes.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-5 rounded-sm border border-[#5EEAD4]/30 bg-[#5EEAD4]/[0.05]"
               data-testid="partner-contract-accept-block">
            <div className="text-[#F7FAFC] font-semibold mb-2">Accept the Partner Agreement</div>
            <p className="text-[#9CA8B8] text-[13px] leading-[1.7] mb-4">
              Please download and read the full Partner Agreement PDF above. By ticking the box below you
              confirm you have read, understood and accept the terms — including the 30-day trial period,
              10% commission, and the post-trial review of monthly fees and commercial terms under clause 5.
              Your acceptance is recorded with timestamp, IP address and user-agent for audit purposes.
            </p>
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="partner-accept-tick-label">
              <input
                type="checkbox"
                checked={acceptTicked}
                onChange={(e) => { setAcceptTicked(e.target.checked); setAcceptErr(null); }}
                className="mt-1 w-4 h-4 accent-[#A6FF00] cursor-pointer"
                data-testid="partner-accept-tick"
              />
              <span className="text-[#D5DCE8] text-[13px] leading-[1.6]">
                I confirm I am authorised to accept this agreement on behalf of{" "}
                <strong className="text-[#F7FAFC]">{me.company_name}</strong> and that I accept the Peptigen Labs
                Partner Agreement <strong className="text-[#F7FAFC]">{me.contract_version}</strong>.
              </span>
            </label>
            {acceptErr && (
              <div className="mt-3 text-[#FF4E5A] text-[12px]" data-testid="partner-accept-error">{acceptErr}</div>
            )}
            <button
              type="button"
              onClick={acceptContract}
              disabled={!acceptTicked || acceptSubmitting}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#A6FF00] text-[#0B1220] font-bold text-[13px] tracking-[0.05em] uppercase disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#B6FF24] transition-colors"
              data-testid="partner-accept-submit"
            >
              {acceptSubmitting ? "Recording…" : "Accept Agreement"}
            </button>
          </div>
        )}
      </section>

      {/* Compliance reminder — important: every UK research partnership
          lives or dies on whether the partner's creatives stay inside
          CAP Code + research-use-only positioning. */}
      <aside className="mt-10 p-5 rounded-sm border border-[#5EEAD4]/30 bg-[#5EEAD4]/[0.05]">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#5EEAD4] shrink-0 mt-1" />
          <div>
            <div className="text-[#F7FAFC] font-semibold mb-1">Compliance reminder</div>
            <p className="text-[#9CA8B8] text-[13px] leading-[1.7]">
              All advertising creative must comply with the UK ASA / CAP Code and the
              Peptigen Labs research-use-only positioning. Therapeutic, medical, dosing
              and human-consumption claims are NOT permitted. All creatives must be
              pre-approved by Peptigen Labs before going live. See your contract for
              full content compliance terms.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, ...rest }) {
  return (
    <div className="pg-card p-4" {...rest}>
      <div className="flex items-center gap-2 pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4] mb-1">
        <Icon size={12} /> {label}
      </div>
      <div
        className={`pg-heading text-2xl ${accent ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`}
        style={{ fontFamily: "'Rajdhani'" }}
      >
        {value}
      </div>
    </div>
  );
}
